import { Text } from 'blaze-ui/Text/Text'
import Styles from './StyleImageLoader.module.scss'
import cx from 'classnames'
import { MediaFile } from 'components/VisualStylePreviewCarousel/VisualStylePreviewCarousel'
import { useState, useEffect, useRef, useCallback } from 'react'
import { ProgressBar } from 'components/ProgressBar/ProgressBar'

export interface StyleImageLoaderProps {
  sourceImage?: MediaFile
  stylesGeneration?: { styledUrl?: string | null; isLoading: boolean; hasFailed: boolean }
  onTransformedImageLoaded?: (loaded: boolean) => void
  withTag?: boolean
  fakeDuration?: number
  staticImageUrl?: string
  staticMode?: boolean
}

const FAKE_PROGRESS_MAX = 95
const FINAL_IMAGE_DELAY = 800

export const StyleImageLoader = ({
  sourceImage,
  stylesGeneration,
  onTransformedImageLoaded,
  withTag = true,
  fakeDuration = 50000,
  staticImageUrl,
  staticMode = false,
}: StyleImageLoaderProps) => {
  const [percentage, setPercentage] = useState(0)
  const [showFinalImage, setShowFinalImage] = useState(staticMode)
  const [imageReady, setImageReady] = useState(staticMode)
  const hasNotifiedRef = useRef(false)
  const hasStartedFinalAnimationRef = useRef(false)
  const fakeProgressAnimationRef = useRef<number | null>(null)
  const finalImageRef = useRef<HTMLImageElement>(null)

  const transformedImage = staticMode ? staticImageUrl : stylesGeneration?.styledUrl
  const originalImage = sourceImage?.url

  useEffect(() => {
    if (staticMode) return

    const startTime = Date.now()
    let animationFrame: number

    const animate = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min((elapsed / fakeDuration) * FAKE_PROGRESS_MAX, FAKE_PROGRESS_MAX)
      setPercentage(Math.round(progress))

      if (progress < FAKE_PROGRESS_MAX) {
        animationFrame = requestAnimationFrame(animate)
        fakeProgressAnimationRef.current = animationFrame
      }
    }

    animationFrame = requestAnimationFrame(animate)
    fakeProgressAnimationRef.current = animationFrame

    return () => {
      if (fakeProgressAnimationRef.current) {
        cancelAnimationFrame(fakeProgressAnimationRef.current)
        fakeProgressAnimationRef.current = null
      }
    }
  }, [fakeDuration, staticMode])

  useEffect(() => {
    if (staticMode || !transformedImage || imageReady) return

    const img = new Image()
    img.onload = () => setImageReady(true)
    img.onerror = () => setImageReady(true)
    img.src = transformedImage
  }, [transformedImage, imageReady, staticMode])

  const completeProgress = useCallback(() => {
    setShowFinalImage(true)
    if (!hasNotifiedRef.current && onTransformedImageLoaded) {
      hasNotifiedRef.current = true
      onTransformedImageLoaded(true)
    }
  }, [onTransformedImageLoaded])

  useEffect(() => {
    if (staticMode || !transformedImage || !imageReady) return
    if (hasStartedFinalAnimationRef.current) return

    hasStartedFinalAnimationRef.current = true

    if (fakeProgressAnimationRef.current) {
      cancelAnimationFrame(fakeProgressAnimationRef.current)
      fakeProgressAnimationRef.current = null
    }

    const startPercentage = percentage
    let currentProgress = startPercentage
    let animationFrame: number

    const animate = () => {
      const remainingProgress = 100 - startPercentage
      currentProgress += Math.max(2, remainingProgress * 0.1)

      if (currentProgress >= 100) {
        setPercentage(100)
        setTimeout(completeProgress, FINAL_IMAGE_DELAY)
      } else {
        setPercentage(Math.round(currentProgress))
        animationFrame = requestAnimationFrame(animate)
      }
    }

    animationFrame = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(animationFrame)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transformedImage, imageReady, completeProgress, staticMode])

  const getCurrentStage = () => {
    if (percentage < 30) return 'Analyzing source image'
    if (percentage < 55) return 'Generating new image'
    if (percentage < 85) return 'Applying style'
    return 'Finalizing'
  }

  const isLoading = !staticMode && !showFinalImage

  return (
    <div className={cx(Styles.loadingContainer, { [Styles.loading]: isLoading })}>
      {!staticMode && originalImage && (
        <img
          src={originalImage}
          alt="transforming original"
          className={cx(Styles.originalImage, { [Styles.visible]: isLoading })}
          onContextMenu={(e) => e?.preventDefault()}
        />
      )}
      {transformedImage && (
        <img
          ref={finalImageRef}
          src={transformedImage}
          alt="real transformed"
          className={cx(Styles.finalImage, { [Styles.visible]: showFinalImage })}
          onContextMenu={(e) => e?.preventDefault()}
        />
      )}
      {withTag && <div className={cx(Styles.imageTag, Styles.generatedTag)}>Generated Image</div>}
      {isLoading && (
        <div className={Styles.progressContainer}>
          <ProgressBar
            percentage={percentage}
            color="var(--light-100)"
            backgroundColor="var(--dark-15)"
            size="xs"
            width="112px"
          />
          <Text color="var(--light-60)">
            {getCurrentStage()}... {percentage}%
          </Text>
        </div>
      )}
    </div>
  )
}
